import React, { useState } from 'react';
import { Input, Button, Alert } from '../ui';

interface ServerFormProps {
  onSubmit: (serverName: string, url: string, clientId?: string) => Promise<void>;
  onCancel: () => void;
  clients?: Array<{ id: string; name: string }>;
}

export const ServerForm: React.FC<ServerFormProps> = ({
  onSubmit,
  onCancel,
  clients = []
}) => {
  const [serverName, setServerName] = useState('');
  const [url, setUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!serverName.trim()) {
      setError('Server name is required');
      return;
    }

    if (!url.trim()) {
      setError('Server URL is required');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError('Invalid URL format');
      return;
    }

    try {
      setLoading(true);
      await onSubmit(serverName.trim(), url.trim(), clientId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to connect server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Input
        label="Server Name"
        value={serverName}
        onChange={(e) => setServerName(e.target.value)}
        required
        fullWidth
        placeholder="My MCP Server"
        helperText="A friendly name for this server"
      />

      <Input
        label="Server URL"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        required
        fullWidth
        placeholder="https://example.com/mcp or http://localhost:3000/sse"
        helperText="HTTP or SSE endpoint URL (auto-detected)"
      />

      {clients.length > 0 && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Client (Optional)
          </label>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <option value="">No client (default)</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Associate this server with a specific client
          </p>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
        <p className="text-xs text-blue-800">
          <strong>Supported protocols:</strong> HTTP and SSE (Server-Sent Events) are automatically detected.
          The server will determine the connection type based on the endpoint response.
        </p>
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={!serverName.trim() || !url.trim()}
        >
          Connect Server
        </Button>
      </div>
    </form>
  );
};
